import { Component } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { map, Observable, of, take } from 'rxjs';
import { Group } from '../../shared/entities/group/group';
import { Location } from '../../shared/entities/location/location';
import { User, UserInput } from '../../shared/entities/user/user';
import { GroupsService } from '../../shared/services/groups/groups.service';
import { LoadingService } from '../../shared/services/loading.service';
import { LocationService } from '../../shared/services/location/location.service';
import { UsersService } from '../../shared/services/users/users.service';
import { EditDialogComponent } from '../editDialog/editDialogComponent';
import { EditDialogInstance } from '../editDialog/editDialogService';

@Component({
  selector: 'app-user-dialog',
  templateUrl: './user-dialog.component.html',
  styleUrls: ['./user-dialog.component.scss']
})
export class UserDialogComponent
  implements EditDialogInstance {

    userForm: FormGroup;
    user: User;
    groups: Group[];
    locations: Location[];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public userService: UsersService,
    public groupService: GroupsService,
    public loadingService: LoadingService,
    public locationService: LocationService
  ) {
    this.groupService.getGroups().pipe(
      take(1)
    ).subscribe((groups) => {
      this.groups = groups;
    });
    this.locationService.getLocations().pipe(
      take(1)
    ).subscribe((locations) => {
      this.locations = locations;
    });

    if (this.config.data) {
      this.user = this.config.data;
    }

    this.userForm = new FormGroup({});
    this.userForm.addControl('id', new FormControl());
    // add form controls
    this.userForm.addControl('email', new FormControl(undefined,
      [
        Validators.required,
        Validators.pattern(
          /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,30}$/
        ),
      ],
      [this.validateEmailNotTaken.bind(this)]
    ));
    this.userForm.addControl('initials', new FormControl());
    this.userForm.addControl('firstName', new FormControl(undefined, Validators.required));
    this.userForm.addControl('lastName', new FormControl(undefined, Validators.required));
    this.userForm.addControl('groupIds', new FormControl());
    this.userForm.addControl('locationIds', new FormControl());
    this.userForm.addControl('roles', new FormControl());
    this.userForm.addControl('externalId', new FormControl());

    // if we are creating a new user, we need to add the password field
    if(!this.user) {
      this.userForm.addControl('password', new FormControl(undefined, Validators.required));
      this.userForm.addControl('requiredActions', new FormControl(['UPDATE_PASSWORD']));
    }

    if (this.user) {
      this.userForm.patchValue(this.user);
      // overwrite the roles with the role ids
      let roleIds = this.user.roles.map(role => role.id);
      this.userForm.get('roles').setValue(roleIds);
      // add the group ids
      let groupIds = this.user.groups.map(group => group.id);
      this.userForm.get('groupIds').setValue(groupIds);
    }

    // this.userForm.valueChanges.subscribe((value) => {
    //   console.log(value);
    // });
  }


  onConfirm(editDialogComponent: EditDialogComponent<any>): void {
    this.loadingService.setLoading(true);
    let userInput = new UserInput(this.userForm.value);
    if(this.user){
      this.userService.updateUser(userInput, (result: any) => {
        this.loadingService.setLoading(false);
        this.ref.close(result?.data?.updateUser?.id);
      });
    } else {
      this.userService.createUser(userInput, (result: any) => {
        console.log(result);
        this.loadingService.setLoading(false);
        this.ref.close(result?.data?.createUser?.id);
      });
    }
  }

  onReject(editDialogComponent: EditDialogComponent<any>): void {
    this.ref.close(false);
  }

  getEditForm(): FormGroup {
    return this.userForm;
  }

    /**
   * Validtor for checking if an email address is already taken as login
   * name
   * @param control Abstract
   * @returns emailExists
   */
    validateEmailNotTaken(
      control: AbstractControl
    ): Observable<ValidationErrors | null> {
      // catch the case if someone is just editing an already existing user
      if (this.user && control.value === this.user.email) {
        return of(null);
      }
      const user = new User();
      user.email = control.value;
      return this.userService.checkEmailNotTaken(control.value).pipe(
        map((res) => {
          return !res ? { emailExists: !res } : null;
        })
      );
    }

}
